import mapping, {
    jobTypeMapping,
    graduationMapping,
    graduationCategory,
} from "@/helpers/mapping.js"
import mappingService from "@/services/mapping.service"
import jobFeatures from "@/mixins/jobFeatures"
import { APPRENTICESHIP, TRAINEESHIP, JOB } from "@/enums/jobtypes"
import { getApprenticeshipTypeName } from "@/helpers/job"

const JOB_TYPE = mapping.mapValue(jobTypeMapping)
const GRADUATION = mapping.mapValue(graduationMapping)
const GRADUATION_CATEGORY = mapping.mapValue(graduationCategory)

const OABAT_BASE_INFO = {
    ADDRESS: {
        "@type": "PostalAddress",
        addressLocality: "Passau",
        postalCode: "94032",
        addressRegion: "Bayern",
        streetAddress: "Innstraße 69b",
        addressCountry: "Deutschland",
    },
    URL: "https://www.oabat.de",
    INSTAGRAM: "https://www.instagram.com/oabat/",
    FACEBOOK: "https://www.facebook.com/oabat",
}

export default {
    mixins: [jobFeatures],
    data: () => ({
        forceInternship: false,
    }),
    methods: {
        getImageSrc(file) {
            return this.$getImage({
                image: file,
                preferedSize: "1",
            }).src
        },
        getMetaImage(job) {
            if (job.image)
                return this.$getOGImage({
                    image: job.image,
                })
            if (job.video?.thumbnail_url)
                return {
                    src: job.video?.thumbnail_url,
                    type: "image/avif",
                }
            return {}
        },
        getStructuredDataForJob(job, isInternship) {
            if (!this.jobLoaded) return undefined
            const baseURL = this.$config.baseURL
            const estimatedSalary = job.estimated_salary
            const path = this.$route.path
            const address =
                job.business.business_site?.address ??
                job.business.primary_address ??
                {}
            let result = {
                "@context": "http://schema.org/",
                "@type": "JobPosting",
                title: _jobFullName(job, isInternship),
                hiringOrganization: {
                    "@type": "Organization",
                    name: job.business_site.name,
                    sameAs: `${baseURL}/betriebe/${job.business.slug}`,
                    url: `${baseURL}/betriebe/${job.business.slug}`,
                    logo: this.getImageSrc(job.business.logo)?.replace(
                        "webp",
                        "jpg"
                    ),
                    address: _getPostalAddressSchema(address),
                },
                employerOverview: job.business.about_us ?? "",
                employmentType: isInternship ? "INTERN" : "FULL_TIME",
                jobBenefits: _advantageString(job.advantages),
                jobLocation: {
                    "@type": "Place",
                    address: _getPostalAddressSchema(address),
                },
                qualifications: job.attributes?.join(", ") ?? "",
                skills: job.attributes?.join(", ") ?? "",
                jobStartDate: job.date_of_start,
                salaryCurrency: "EUR",
                image: this.getMetaImage(job).src?.replace("webp", "jpg"),
                validThrough:
                    job.date_application_deadline || _deadlinePlaceholder(),
                datePosted: job.published_at_google_jobs || job.published_at,
                description: _getDescription(job, isInternship),
                baseSalary: _getBaseSalary(job, isInternship),
                url: baseURL + path,
                mainEntityOfPage: _getMainEntity(baseURL),
                directApply: job.use_external_site ? undefined : "True",
                educationRequirements: _getEducationalCredential(
                    job.required_graduation
                ),
            }
            if (
                job.type_id !== APPRENTICESHIP ||
                job.apprenticeship?.salaries[0] ||
                !estimatedSalary ||
                isInternship
            ) {
                return result
            }

            return result
        },
        getStructuredData(job) {
            let result = []
            const baseURL = this.$config.baseURL
            const path = this.$route.path
            const estimatedSalary = job.estimated_salary

            if (job.type_id === APPRENTICESHIP) {
                let apprenticeshipData = this.getStructuredDataForJob(
                    job,
                    this.forceInternship
                )
                if (apprenticeshipData) {
                    if (
                        !job.business.is_education_institute &&
                        estimatedSalary
                    ) {
                        apprenticeshipData.estimatedSalary =
                            _getEstimatedSalary(estimatedSalary)
                    }
                    result.push(apprenticeshipData)
                    if (estimatedSalary) {
                        result.push(_getOccupationSchema(job, baseURL, path))
                    }
                }
            } else {
                // regular jobs
                result.push(this.getStructuredDataForJob(job, false))
            }

            result.push(_getOrganisationSchema(baseURL))
            result.push(_getWebsiteSchema(baseURL))
            return result
        },
        getMetaTitle(job) {
            return `${_jobTypeName(job)} ${job.name} bei ${_businessMetaName(
                job.business_site.name
            )} in ${job.business_site.address.city}`
        },
        getMetaTags(job) {
            const metaImage = this.getMetaImage(job)
            const metaDescription = _metaDescription(job, this.forceInternship)
            return [
                {
                    hid: "description",
                    name: "description",
                    content: metaDescription,
                },
                {
                    hid: "og:description",
                    name: "og:description",
                    content: metaDescription,
                },
                {
                    hid: "og:title",
                    name: "og:title",
                    content: job.name,
                },
                {
                    hid: "og:image",
                    name: "og:image",
                    content: metaImage.src,
                },
                {
                    hid: "og:image:type",
                    name: "og:image:type",
                    content: metaImage.type,
                },
            ]
        },
        getScript(job) {
            let breadcrumbItems = [
                {
                    identifier: "business",
                    business: job.business,
                },
                { identifier: "job", job: job },
            ]
            if (this.forceInternship) {
                breadcrumbItems.push({
                    identifier: "internship",
                    job: job,
                })
            }
            const script = [
                {
                    innerHTML: JSON.stringify(
                        this.getBreadcrumb(breadcrumbItems)
                    ),
                    type: "application/ld+json",
                },
            ]
            if (this.featureGoogleJobs) {
                script.push({
                    innerHTML: JSON.stringify(this.getStructuredData(job)),
                    type: "application/ld+json",
                })
            }
            return script
        },
    },
}

function _getMainEntity(baseURL) {
    return {
        "@type": "WebPage",
        "@id": baseURL,
        alternateName: "Oabat",
        sameAs: [
            OABAT_BASE_INFO.FACEBOOK,
            OABAT_BASE_INFO.INSTAGRAM,
            OABAT_BASE_INFO.URL,
        ],
    }
}

function _getBaseSalary(job, isInternship) {
    const baseSalary = job.apprenticeship?.salaries[0]
    if (!baseSalary || job.business.is_education_institute || isInternship)
        return undefined
    return {
        "@type": "MonetaryAmount",
        currency: "EUR",
        value: {
            "@type": "QuantitativeValue",
            value: baseSalary?.toFixed(2),
            unitText: "MONTH",
        },
    }
}

function _getEducationalCredential(graduation) {
    if (graduation === "none") return undefined
    return {
        "@type": "EducationalOccupationalCredential",
        credentialCategory: GRADUATION_CATEGORY(graduation) ?? "high school",
        educationalLevel: GRADUATION(graduation) ?? "Kein Schulabschluss",
    }
}

function _jobTypeName(job, forceInternship = false) {
    if (forceInternship) return "Praktikum"
    if (job.type_id === APPRENTICESHIP)
        return getApprenticeshipTypeName(job.apprenticeship)
    return JOB_TYPE(job.type_id)
}

function _jobFullName(job, forceInternship = false) {
    return `${_jobTypeName(job, forceInternship)} ${job.name} (m/w/d)`
}

function _advantageString(advantages) {
    if (!advantages) return ""
    let value = ""
    if (advantages.length > 0) {
        value = "<ul>"
        advantages.forEach((a) => {
            value += `<li>${a}</li>`
        })
        value += "</ul>"
    }
    return value
}

function _deadlinePlaceholder() {
    let date = new Date()
    date.setDate(date.getDate() + 30)
    return new Intl.DateTimeFormat("en", {
        dateStyle: "medium",
    }).format(date)
}

function _getContentBlocks(contentBlocks) {
    try {
        const blocks = contentBlocks[0].data.items
        return blocks.map((block) => ({
            text: block.data.text,
            headline: block.data.headline,
        }))
    } catch {
        return []
    }
}

function _getContentBlocksTraineeshipData(contentBlocks) {
    try {
        return contentBlocks.map((block) => ({
            text: block.text,
            headline: block.headline,
        }))
    } catch {
        return []
    }
}

function _getDescription(job, isInternship) {
    let desc = `<p>${_jobFullName(job, isInternship)} bei ${
        job?.business?.name
    } am Standort ${job?.business_site?.address.city}</p><br>`

    const contactPerson = job.business.public_contact_person,
        responseTime = job.business.avg_response_time
    let contactInfo = contactPerson
        ? `<p>Dein Ansprechparter ${contactPerson.gender} ${contactPerson.firstname} ${contactPerson.lastname}</p>\n`
        : ""
    contactInfo += responseTime
        ? `<p>antwortet dir über Oabat in ${mappingService.mapBusinessResponseTime(
              responseTime
          )}h</p>\n\n`
        : contactPerson
        ? "\n"
        : ""

    if (isInternship && job.traineeship_data?.content_blocks?.length > 0) {
        for (const { text, headline } of _getContentBlocksTraineeshipData(
            job.traineeship_data.content_blocks
        )) {
            desc += `<p>${headline}</p>${text}\n\n`
        }
    } else {
        if (job.content_blocks?.length > 0) {
            for (const { text, headline } of _getContentBlocks(
                job.content_blocks
            )) {
                desc += `<p>${headline}</p>${text}\n\n`
            }
            desc += contactInfo
            return desc
        }
    }

    if (job.description_work?.length > 0) {
        desc += `<p>Wie Dein Alltag als ${job.name} aussehen wird</p>\n${job.description_work}\n\n`
    }
    if (job.description_education?.length > 0) {
        desc += `<p>Was Du als ${job.name} bei ${job.business.name} lernen wirst</p>\n${job.description_education}\n\n`
    }
    if (job.trainer?.description?.length > 0) {
        desc += `<p>Zu Deinem Ausbilder am Standort in ${job.business_site.address.city}</p>\n${job.trainer.description}\n\n`
    }
    if (job.description_important?.length > 0) {
        desc += `<p>Was Du noch unbedingt zu deinem Job als ${job.name} wissen solltest</p>\n${job.description_important}\n\n`
    }
    desc += contactInfo
    return desc
}

function _getPostalAddressSchema(address) {
    return {
        "@type": "PostalAddress",
        addressCountry: address.country_code,
        postalCode: address.postal_code,
        streetAddress: address.street,
        addressLocality: address.city,
        addressRegion: address.state,
    }
}

function _getWebsiteSchema(baseURL) {
    return {
        "@context": "http://schema.org",
        "@type": "WebSite",
        name: "Oabat - Stellenb&#246;rse f&#252;r Ausbildung, Sch&#252;lerpraktika und Duales Studium",
        alternateName: "Oabat",
        url: baseURL,
        sameAs: [OABAT_BASE_INFO.FACEBOOK, OABAT_BASE_INFO.INSTAGRAM],
    }
}

function _getOrganisationSchema(baseURL) {
    return {
        "@context": "http://schema.org",
        "@type": "Organization",
        name: "Oabat",
        legalName: "Oabat GmbH",
        url: baseURL,
        logo: baseURL + require("@/static/icon.png"),
        sameAs: [
            OABAT_BASE_INFO.FACEBOOK,
            OABAT_BASE_INFO.INSTAGRAM,
            OABAT_BASE_INFO.URL,
        ],
        description:
            "Die Stellenb&#246;rse f&#252;r Ausbildung, Sch&#252;lerpraktika und Duales Studium",
        address: OABAT_BASE_INFO.ADDRESS,
        location: {
            "@type": "Place",
            address: OABAT_BASE_INFO.ADDRESS,
        },
    }
}

function _getEstimatedSalary(estimatedSalary) {
    return {
        "@type": "MonetaryAmountDistribution",
        name: "base",
        currency: "EUR",
        duration: "P1M",
        percentile10: estimatedSalary.percentile10,
        percentile25: estimatedSalary.percentile25,
        median: estimatedSalary.avg,
        percentile75: estimatedSalary.percentile75,
        percentile90: estimatedSalary.percentile90,
        minValue: estimatedSalary.min,
        maxValue: estimatedSalary.max,
    }
}

function _metaDescription(job, forceInternship) {
    if (job.description_work) {
        return _truncate(job.description_work, 150)
    }
    const businessName = _businessMetaName(job.business_site.name)
    const businessCity = job.business_site.address.city
    const contentSections = _getContentBlocks(job.content_blocks)
    if (Number(job.version) === 2 && contentSections.length > 0) {
        let desc = ""
        if (job.type_id === APPRENTICESHIP) {
            const apprenticeshipName = _jobTypeName(job)
            desc = job.traineeship_data?.is_active
                ? `In deiner ${apprenticeshipName} / deinem Praktikum zum ${job.name} bei ${businessName} in ${businessCity} lernst du: `
                : `In der ${apprenticeshipName} zum ${job.name} bei ${businessName} in ${businessCity} lernst du: `
        } else if (job.type_id === TRAINEESHIP) {
            desc = `Im Praktikum zum ${job.name} bei ${businessName} in ${businessCity} kannst du ausprobieren: `
        } else {
            desc = `Als ${job.name} bei ${businessName}  in ${businessCity} machst du: `
        }
        for (const { text, headline } of contentSections) {
            desc += `${headline}. ${text}`
        }
        return _truncate(desc, 150)
    }
    let metaJobType = ""
    if (forceInternship) {
        return `Starte dein Praktikum als ${job.name} bei ${businessName} am Standort ${businessCity}.`
    }
    if (job.type_id !== JOB) {
        const prefix = job.type_id === APPRENTICESHIP ? "zur" : "zum"
        metaJobType = ` ${prefix} ${_jobTypeName(job)}`
    }
    return `Finde alles über die freie Stelle${metaJobType} als ${job.name} bei ${businessName} am Standort ${businessCity} heraus.`
}

function _businessMetaName(name) {
    const matches = name.match(/(.*) gmbh/i)
    if (matches && matches[1]) return matches[1]
    return name
}

function _getOccupationSchema(job, baseURL, path, isInternship) {
    const estimatedSalary = job.estimated_salary
    return {
        "@context": "https://schema.org/",
        "@type": "Occupation",
        name: _jobFullName(job, isInternship),
        mainEntityOfPage: {
            "@type": "WebPage",
            "@id": baseURL + path,
            lastReviewed: estimatedSalary.calculated_at,
        },
        sampleSize: estimatedSalary.sampleSize,
        description: _getDescription(job, isInternship),
        estimatedSalary: [_getEstimatedSalary(estimatedSalary)],
        jobBenefits: _advantageString(job.advantages),
        occupationLocation: [
            {
                "@type": "City",
                name:
                    job.business_site?.address.city ||
                    job.business?.primary_address?.city,
            },
        ],
    }
}

function _truncate(text, max) {
    return text.slice(0, max - 1) + (text.length > max ? "..." : "")
}
